/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/forbid-prop-types */
import React, { useState, useEffect } from 'react';
import Helmet from 'react-helmet';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';

import Button from 'components/Button';
import EmailInput from 'components/forms/EmailInput';
import Heading from 'components/Heading';
import { IconEmailReset } from 'components/Icons';
import Logo from 'components/Logo';
import Paragraph from 'components/Paragraph';
import PropTypes from 'prop-types';
import { apiInstance } from 'services';
import logger from 'services/logger/logger';

import { useTheme } from '../../context/theme-context';

import styles from './ForgotPasswordScreen.module.scss';

const APIService = apiInstance();
const {
  generateUUIDV4,
  namespaces: { onboarding }
} = APIService;

const ForgotPasswordScreen = ({ location: { state } }) => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [emailAddress, setEmailAddress] = useState(state?.emailAddress);
  const [emailResent, setEmailResent] = useState(false);

  const { getCurrentTheme } = useTheme();
  const { homepage_logo } = getCurrentTheme();

  const { formState, register, handleSubmit, errors } = useForm({
    mode: 'onChange'
  });
  const { touched, isValid, dirtyFields } = formState;
  const history = useHistory();

  const requestPassword = (email) => {
    const token = generateUUIDV4();
    localStorage.setItem('cureoscity_password_reset_token1', token);

    return onboarding.requestPassword(token, email);
  };

  const onSubmit = (data) => {
    const { email } = data;
    setEmailAddress(email);
    // send email to password recovery
    requestPassword(email)
      .then(() => setFormSubmitted(true))
      .catch((error) => logger.debug(error));
  };

  const renderPasswordResetForm = () => (
    <>
      <Heading appearance="h4">Forgot password?</Heading>
      <Paragraph centered className={styles.paragraph}>
        If you have forgotten your password, enter your registered email address
        below......
      </Paragraph>
      <form onSubmit={handleSubmit(onSubmit)}>
        <EmailInput
          {...{
            register,
            errors,
            touched,
            dirtyFields,
            name: 'email'
          }}
        />
        <Button
          type="submit"
          text="Send"
          size="full-width"
          width="100%"
          disabled={!isValid}
        />
      </form>
      <Button
        text="Help"
        appearance="text"
        size="full-width"
        width="100%"
        onClick={() => history.push('/help')}
      />
    </>
  );

  const changeEmail = () => {
    setFormSubmitted(false);
    setEmailResent(false);
  };

  const renderNextSteps = () => (
    <>
      {emailResent && (
        <span className={styles.alert}>Email was successfully resent</span>
      )}
      <IconEmailReset size="110px" />
      <Heading appearance="h4">Check your email</Heading>
      <Paragraph centered className={styles.paragraph}>
        We just need to check that it's really you! Please click 'Verify Email'
        and enter the 4-digit code sent to your inbox. From there you can then
        reset your password!
        <br />
        <span className={styles.email}>{emailAddress}</span>
      </Paragraph>
      <Button
        text="Verify email"
        appearance="primary"
        size="full-width"
        onClick={() =>
          history.push({ pathname: '/verify', state: { emailAddress } })
        }
        width="100%"
      />
      <Button
        size="inline-small"
        text="Back to login"
        appearance="text"
        onClick={() => {
          history.push('/');
        }}
      />
      <Paragraph className={`${styles['sub-option']} ${styles.first}`}>
        Didn&apos;t get the email?
        <Button
          size="inline-small"
          text="Resend"
          appearance="text"
          onClick={() =>
            requestPassword(emailAddress).then(() => setEmailResent(true))
          }
        />
      </Paragraph>
      <Paragraph className={styles['sub-option']}>
        Wrong email address?
        <Button
          size="inline-small"
          text="Change"
          appearance="text"
          onClick={changeEmail}
        />
      </Paragraph>
    </>
  );

  useEffect(() => {
    // Handle resend email functionality
    if (state?.emailAddress) {
      setFormSubmitted(true);
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>Forgot password</title>
      </Helmet>
      <div className={styles.logo}>
        <Logo size="large" url={`${homepage_logo}`} altText="logo" />
      </div>
      <div className={styles.wrapper}>
        {formSubmitted ? renderNextSteps() : renderPasswordResetForm()}
      </div>
    </>
  );
};

ForgotPasswordScreen.propTypes = {
  location: PropTypes.object
};

ForgotPasswordScreen.defaultProps = {
  location: false
};

export default ForgotPasswordScreen;
